/**
 * A phone number the way it is stored: a leading `+` and nothing but digits.
 *
 * The form's chip saves E.164 on its own, but a number reaches a record by
 * other doors too — a seed, the assistant, a link copying a text field — and
 * each of them writes it the way a person reads it: `+91 82040 83754`. The
 * spaces (the no-break one included), dashes, dots and brackets in that are
 * how a number is written down, not part of it, so they go.
 *
 * Only a number that names its country is touched, and only when what is left
 * is digits. A bare national number names no country and nothing here guesses
 * one, so it comes back as sent. So does `+91 abc`: it is not a number, and
 * tidying it would make it look like one. Both are only trimmed.
 *
 * The server stores every phone through it (`castScalar` in
 * `apps/server/src/entry-values.ts`), and the assistant's proposals carry the
 * same form, so a `+` number is stored one way whichever door it came through.
 * The phone field splits a stored value through it too, so a row written
 * before the server tidied it — `+91 8204083754` — still opens with the dial
 * code on the chip and the national digits in the box, rather than the whole
 * value in the box beside a chip that already says +91.
 */

/** `\s` already holds U+00A0, the space a copied contact card carries. */
const SEPARATORS = /[\s().-]/g;

export const canonicalPhone = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('+')) return trimmed;
  const digits = trimmed.slice(1).replace(SEPARATORS, '');
  return /^\d+$/.test(digits) ? `+${digits}` : trimmed;
};
